import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import Icon from './AppIcon';
import LegalNoticeModal from './modals/LegalNoticeModal';
import {ExternalLinkIcon} from "lucide-react";

/**
 * LOCKED DESIGN SYSTEM COMPONENT
 *
 * This Footer component is part of the Sleeve Design System.
 * It CANNOT be regenerated or visually altered outside the Design System.
 *
 * To modify this component:
 * → Admin → Design System → Components → Footer
 *
 * Page-level prompts may only:
 * - Reference this component
 * - Toggle visibility if needed
 *
 * Any visual or structural changes require Design System approval.
 */
const Footer = () => {
  const currentYear = new Date()?.getFullYear();
  const [isImprintModalOpen, setIsImprintModalOpen] = useState(false);

  const footerLinks = {
    product: [
      {label: 'Documentation', path: 'https://docs.sleeve.dev', external: true},
      {label: 'Sign Up', path: 'https://hub.sleeve.dev', external: true},
      {label: 'Contact', path: '/contact'},
    ],

    company: [
      {label: 'About', path: '/about'},
      {label: 'Careers', path: '/careers'},
      {label: 'Legal Notice | Impressum', modal: true}],

    fundedAndSupported: [
      {
        name: 'Vienna Business Agency',
        url: 'https://viennabusinessagency.at',
        description: 'Supported by Vienna Business Agency',
        logo: '/assets/images/wirtschaftsagentur-1769704657252.png',
        alt: 'Vienna Business Agency logo'
      },
      {
        name: 'FFG',
        url: 'https://www.ffg.at',
        description: 'This project is funded by the FFG',
        logo: '/assets/images/FFG_Logo_EN_RGB_1500px-1770285370863.png',
        alt: 'FFG logo'
      },
      {
        name: 'AWS',
        url: 'https://www.aws.at',
        description: 'This project is funded by the AWS',
        logo: '/assets/images/AWS_DeepTech-1769698694455.jpg',
        alt: 'Austria Wirtschaftsservice (aws) logo'
      }],

    socialLinks: [
      {name: "LinkedIn", url: "https://www.linkedin.com/company/sleeve-gmbh/"},
      {name: "Discord", url: "https://discord.gg/8wX79raDyf"}
    ]

  };

  const handleImprintClick = (e) => {
    e?.preventDefault();
    setIsImprintModalOpen(true);
  };

  return (
    <>
      <footer className="border-t border-border bg-card/30">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-12 md:py-16 lg:py-20">
          {/* Main Footer Content */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 md:gap-10 lg:gap-12 mb-8 md:mb-10 lg:mb-12">
            {/* Brand Column */}
            <div className="lg:col-span-2">
              <Link to="/homepage" className="inline-flex items-center gap-3 mb-4 md:mb-5">
                <img
                  src="/assets/images/SLEEVE-logo-white-1769672699589.png"
                  alt="Sleeve"
                  className="h-10 w-auto"/>

              </Link>
              <p className="text-xs md:text-sm text-muted-foreground mb-4 md:mb-5 leading-relaxed max-w-xs">
                Orchestrate distributed intelligence across cloud, edge, and legacy systems.
              </p>

              {/* Social Links */}
              <div className="flex items-center gap-3">
                {footerLinks?.socialLinks?.map((link) => (
                  <a
                    key={link.name}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 rounded-lg bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors duration-250"
                    aria-label={link.name}>

                    <Icon name={link.name} size={20} color="var(--color-primary)"/>
                  </a>
                ))}
              </div>
            </div>

            {/* Product Links */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-3 md:mb-4">Product</h3>
              <ul className="space-y-2 md:space-y-3">
                {footerLinks?.product?.map((link, index) =>
                  <li key={index}>
                    {link?.external ?
                      <a href={link?.path}
                         target="_blank"
                         className="flex items-center text-xs md:text-sm text-muted-foreground hover:text-foreground transition-colors duration-250"
                         rel="noopener noreferrer">
                        <span>{link?.label}</span>
                        <ExternalLinkIcon height="16px" className="m-1"></ExternalLinkIcon>
                      </a>
                      :
                      <Link
                        to={link?.path}
                        className="text-xs md:text-sm text-muted-foreground hover:text-foreground transition-colors duration-250">
                        {link?.label}
                      </Link>
                    }
                  </li>
                )}
              </ul>
            </div>

            {/* Company Links */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-3 md:mb-4">Company</h3>
              <ul className="space-y-2 md:space-y-3">
                {footerLinks?.company?.map((link, index) =>
                  <li key={index}>
                    {link?.modal ?
                      <button
                        onClick={handleImprintClick}
                        className="text-xs md:text-sm text-muted-foreground hover:text-foreground transition-colors duration-250 text-left">

                        {link?.label}
                      </button> :

                      <Link
                        to={link?.path}
                        className="text-xs md:text-sm text-muted-foreground hover:text-foreground transition-colors duration-250">

                        {link?.label}
                      </Link>
                    }
                  </li>
                )}
              </ul>
            </div>

            {/* Funded and Supported */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-3 md:mb-4">Funded and Supported</h3>
              <ul className="space-y-3 md:space-y-4">
                {footerLinks?.fundedAndSupported?.map((org, index) =>
                  <li key={index}>
                    <a
                      href={org?.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block group">

                      <div className="flex items-start gap-3">
                        <div
                          className="w-11 h-11 rounded bg-muted flex items-center justify-center flex-shrink-0 group-hover:bg-muted/80 transition-colors duration-250">
                          <img
                            src={org?.logo}
                            alt={org?.alt}
                            className="max-w-[28px] max-h-[28px] object-contain"/>

                        </div>
                        <div className="leading-snug">
                          <p className="text-xs font-medium text-foreground group-hover:text-primary transition-colors duration-250">
                            {org?.name}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {org?.description}
                          </p>
                        </div>
                      </div>
                    </a>
                  </li>
                )}
              </ul>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="pt-6 md:pt-8 border-t border-border">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <p className="text-xs md:text-sm text-muted-foreground text-center md:text-left">
                &copy; {currentYear} Sleeve.dev. All rights reserved.
              </p>
              <Link title="Admin Panel" className="text-xs rounded-md bg-muted md:text-x text-muted-foreground text-center md:text-right"
                    to="/admin-login">
                <Icon className="m-1.5" name="UserShield" size={16} color="var(--color-primary)"/>
              </Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Imprint Modal */}
      <LegalNoticeModal
        isOpen={isImprintModalOpen}
        onClose={() => setIsImprintModalOpen(false)}/>

    </>);

};

export default Footer;